import * as ablyService from '@/services/ablyService';
import * as pubnubService from '@/services/pubnubService';
import {
    createPubnubCatalogOnMessageReceived,
    createPubnubCatalogOnPresence,
    createPubnubCatalogOnStatus,
    hereNow,
} from '@/redux/modules/pubnub';
import filters, { FilterOptions } from './filters';
import { PubnubMessage, PubnubPresenceEvent, PubnubStatusEvent } from '@/types/pubnub';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import { getBrowserSessionCookieSuffix } from '@/redux/modules/console';
import { PubSubProvider } from '@/types/PubSubProvider';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

type Props = {
    /** The catalog to subscribe for real-time events (onMessage, onStatus, onPresence etc) */
    catalogId: number;
    /** Applies a filter on message events to prevent additional work for specific use-cases */
    limitMessagesFilter?: FilterOptions;
    /** Ignore onStatus and onMessage events, useful when presence is the only data needed, default = false */
    presenceOnly?: boolean;
    /** The Pub/Sub service to connect to i.e. Ably or PubNub */
    provider: PubSubProvider;
    publishKey: string;
    /** Publish the current user's presence to pubsub provider and dispatch updates to redux, default = false */
    reportUserPresence: boolean;
    subscribeKey: string;
    /** Subscribe to occupancy updates from pub/sub provider and dispatch those updates to redux */
    subscribeToPresence: boolean;
};

type HookReturnType = {
    connect: () => Promise<void>;
};

/**
 * Creates a PubNub client to connect to ably/pubnub and process auction messages/statuses into redux
 */
const useCatalogPubSub = ({
    catalogId,
    limitMessagesFilter,
    presenceOnly = false,
    provider,
    publishKey,
    reportUserPresence = false,
    subscribeKey,
    subscribeToPresence,
}: Props): HookReturnType => {
    const channel = `catalog.${catalogId}`;
    const dispatch = useDispatch();

    /**
     * PubNub channel instances
     */
    const auctionChannelRef = React.useRef(null);

    /**
     * Generate session cookie for PubNub client
     */
    const browserSession = useSelector(getBrowserSessionCookieSuffix);
    const sessionId = React.useMemo(() => {
        const cookie = new Cookies();
        return `${cookie.get('_session_id') || uuidv4()}${browserSession}`;
    }, [browserSession]);

    /**
     * Handlers for PubNub events
     */
    const onMessage = React.useCallback(
        (message: PubnubMessage) => dispatch(createPubnubCatalogOnMessageReceived(catalogId)(message)),
        [catalogId, dispatch]
    );

    const onPresence = React.useCallback(
        (presenceEvent: PubnubPresenceEvent) => dispatch(createPubnubCatalogOnPresence(catalogId)(presenceEvent)),
        [catalogId, dispatch]
    );

    const onStatus = React.useCallback(
        (statusEvent: PubnubStatusEvent) =>
            dispatch(createPubnubCatalogOnStatus(catalogId, provider, reportUserPresence)(statusEvent)),
        [catalogId, dispatch, provider, reportUserPresence]
    );

    const submitHereNow = React.useCallback(async () => dispatch(hereNow(catalogId, provider)), [
        catalogId,
        dispatch,
        provider,
    ]);

    /**
     * Handle connection and reconnection to PubNub
     */
    const connect = React.useCallback(async () => {
        // Don't try connecting without a subscribeKey
        if (!subscribeKey) {
            return;
        }

        const filterExpression = limitMessagesFilter ? filters[limitMessagesFilter] : '';

        // Unsubscribe to current channels if reconnecting with new keys
        if (auctionChannelRef?.current) {
            await auctionChannelRef.current.unsubscribe();
        }

        if (provider === 'ably') {
            await ablyService.connect(sessionId, subscribeKey);
            const newAuctionChannel = await ablyService.subscribe(
                channel,
                onPresence,
                onMessage,
                onStatus,
                presenceOnly,
                subscribeToPresence
            );
            await ablyService.setFilterExpression(filterExpression);

            auctionChannelRef.current = newAuctionChannel;
        } else if (provider === 'pubnub') {
            await pubnubService.connect(publishKey, sessionId, subscribeKey);
            const newAuctionChannel = await pubnubService.subscribe(
                channel,
                onPresence,
                onMessage,
                onStatus,
                presenceOnly,
                subscribeToPresence
            );
            await pubnubService.setFilterExpression(filterExpression);

            auctionChannelRef.current = newAuctionChannel;
        }

        // Report current user's presence for catalog
        if (reportUserPresence) {
            await submitHereNow();
        }
    }, [
        channel,
        limitMessagesFilter,
        onMessage,
        onPresence,
        onStatus,
        provider,
        presenceOnly,
        publishKey,
        reportUserPresence,
        sessionId,
        submitHereNow,
        subscribeKey,
        subscribeToPresence,
    ]);

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            if (auctionChannelRef?.current) {
                auctionChannelRef.current.unsubscribe();
                auctionChannelRef.current = null;
            }
        };
    }, []);

    return { connect };
};

export default useCatalogPubSub;
